import React, { useState, useEffect } from 'react';
import { alertsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await alertsAPI.list();
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-l-4 border-red-500',
      high: 'bg-orange-100 text-orange-700 border-l-4 border-orange-500',
      medium: 'bg-yellow-100 text-yellow-700 border-l-4 border-yellow-500',
      low: 'bg-green-100 text-green-700 border-l-4 border-green-500'
    };
    return colors[severity] || colors.low;
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="alerts-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Alerts</h1>
        <p className="text-sm text-muted-foreground">Monitor and manage security alerts and notifications</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No alerts found. Your system is running smoothly.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <Card key={alert.id} className={`${getSeverityColor(alert.severity)}`} data-testid={`alert-card-${alert.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <AlertCircle className="w-6 h-6 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{alert.title}</h3>
                        <span className="px-2 py-1 bg-white/50 rounded text-xs font-medium">
                          {alert.severity.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm mb-3">{alert.description}</p>
                      <div className="flex items-center gap-4 text-xs">
                        <span>Source: {alert.source}</span>
                        <span>Status: {alert.status}</span>
                        <span>{new Date(alert.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;